import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import { COLORS } from "../../constants/colors";
import {
  Button,
  Card,
  Chip,
  CircularProgress,
  InputLabel,
  Link,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useGenerateVendorMetadata } from "./BrandVendor/hooks";
import {
  useVendor,
  useVendorMutation
} from "../../hooks/useVendors";
import { useUploadImage } from "../../hooks/useUploadImage";
import { SubcategorySelect } from "./SubcategorySelect";
import {
  VendorLayout,
  VendorHeader,
  VendorContent,
  VendorSidebar
} from "./VendorLayout";
import { VendorSelect } from "./VendorSelect";
import { SidebarModuleCheck, SidebarModule } from "./SidebarModule";
import { CertificationButton } from "./CertificationButton";
import {
  useCreateVendorCertificationMutation,
  useUpdateVendorCertificationMutation
} from "../../hooks/useVendorCertifications";
import {
  useCreateVendorOfferMutation,
  useUpdateVendorOfferMutation,
  useCreateVendorOfferCouponsMutation
} from "../../hooks/useVendorOffers";
import { CertificationModule } from "./CertificationModule";
import { buildOfferPayload, separateObjectByPrefix } from "./utils";
import { OfferModal } from "./OfferModal";
import { ImageUpload } from "../ImageUpload";

import { Info, Delete, Add } from "@mui/icons-material";
import BrandRecommendations from "./BrandRecommendations";
import BrandAttributes from "./BrandAttributes";
import VendorAttributes from "./vendorAttributes";
import { useGenieCollections } from "../../hooks/useGenieCollections";
import { OfferSection } from "./OfferSection";

const CENTER_COLUMN_STYLES = {
  width: "100%"
};

const vendorMetaAttributesRequired = ["description", "avatarURL"];

const VendorInputTip = (props) => (
  <Tooltip
    arrow
    title="Guidelines https://www.notion.so/How-to-update-brand-data-in-Potato-1510bc53a62d8040954efd0ddcf61f4b"
    placement="top-end"
    {...props}
  >
    <Info color="secondary" sx={{ ml: 1 }} />
  </Tooltip>
);

export const Vendor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendorId, setVendorId] = useState(() =>
    id === "new" ? undefined : id
  );
  const selectedOffer = searchParams.get("offer");
  const {
    data,
    isLoading,
    refetch,
    error: vendorFetchError
  } = useVendor(vendorId, {
    enabled: !!vendorId
  });
  const {
    mutateAsync: updateVendor,
    isLoading: isVendorUpdating,
    error: vendorUpdateError
  } = useVendorMutation();
  const { mutateAsync: uploadImage } = useUploadImage();
  const { mutateAsync: createOffer, isLoading: isCreatingOffer } = useCreateVendorOfferMutation(vendorId);
  const { mutateAsync: updateOffer, isLoading: isUpdatingOffer } = useUpdateVendorOfferMutation(vendorId);
  const { data: genieCollectionsData, isLoading: isLoadingGenieCollections } = useGenieCollections();

  const [storedError, setError] = useState();
  const vendor = useMemo(() => data?.vendor ?? {}, [data]);
  const [changes, setChanges] = useState({});
  const [newCollectionId, setNewCollectionId] = useState("");
  const [newCollectionType, setNewCollectionType] = useState("");
  const handleChange = (e) => {
    setError(null);
    const name = e.target.name;
    let value = e.target.value;

    if (name === "companySize" && value === "") {
      value = null;
    }

    // Handle metadata fields (nested properties)
    if (name.includes("metadata.")) {
      const [, metadataKey, offerId] = name.match(/metadata\.(\w+):(.+)/);
      const metadataFieldKey = `metadata:${offerId}`;

      setChanges((prevChanges) => {
        const existingMetadata = prevChanges[metadataFieldKey] || {};
        const updatedChanges = {
          ...prevChanges,
          [metadataFieldKey]: {
            ...existingMetadata,
            [metadataKey]: value
          }
        };

        return updatedChanges;
      });
    } else {
      setChanges((prevChanges) => ({ ...prevChanges, [name]: value }));
    }
  };

  const handleAddCollection = () => {
    if (!newCollectionId) {
      return;
    }

    const newCollection = {
      genieCollectionId: parseInt(newCollectionId, 10),
      type: newCollectionType || null
    };

    setChanges((prevChanges) => {
      const currentCollections = prevChanges.relatedGenieCollections ?? vendor?.Brand?.relatedGenieCollections ?? [];
      return {
        ...prevChanges,
        relatedGenieCollections: [...currentCollections, newCollection]
      };
    });

    setNewCollectionId("");
    setNewCollectionType("");
  };

  const handleRemoveCollection = (index) => {
    setChanges((prevChanges) => {
      const currentCollections = prevChanges.relatedGenieCollections ?? vendor?.Brand?.relatedGenieCollections ?? [];
      const updatedCollections = currentCollections.filter((_, i) => i !== index);
      return {
        ...prevChanges,
        relatedGenieCollections: updatedCollections
      };
    });
  };

  const handleCollectionTypeChange = (index, newType) => {
    setChanges((prevChanges) => {
      const currentCollections = prevChanges.relatedGenieCollections ?? vendor?.Brand?.relatedGenieCollections ?? [];
      const updatedCollections = [...currentCollections];
      updatedCollections[index] = {
        ...updatedCollections[index],
        type: newType || null
      };
      return {
        ...prevChanges,
        relatedGenieCollections: updatedCollections
      };
    });
  };

  const hasNewOffer = useMemo(() => {
    return Object.keys(changes).some(
      (key) => key.startsWith("offer") && key.endsWith("new")
    );
  }, [changes]);
  const { rewardOffers, nonRewardOffers } = useMemo(() => {
    const offers = vendor?.VendorOffers ?? [];

    const sortByStartDateDesc = (a, b) => {
      const dateA = a.offerStartDate ? new Date(a.offerStartDate) : new Date(0);
      const dateB = b.offerStartDate ? new Date(b.offerStartDate) : new Date(0);
      return dateB - dateA;
    };

    const reward = offers
      .filter((offer) => offer.rewardRedemption === true)
      .sort(sortByStartDateDesc);

    const nonReward = offers
      .filter((offer) => offer.rewardRedemption === false)
      .sort(sortByStartDateDesc);

    return {
      rewardOffers: reward,
      nonRewardOffers: nonReward
    };
  }, [vendor?.VendorOffers]);
  const selectOffer = (offerId) => {
    // When creating a new offer, prefill offerUrl with vendor websiteURL
    if (offerId === "new" && vendor?.VendorMeta?.websiteURL) {
      setChanges((prevChanges) => ({
        ...prevChanges,
        [`offerUrl:${offerId}`]: vendor.VendorMeta.websiteURL
      }));
    }

    setSearchParams(
      (params) => {
        params.set("offer", offerId);
        return params;
      },
      { replace: true }
    );
  };

  const closeOffer = () => {
    setSearchParams(
      (params) => {
        params.delete("offer");
        return params;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (id === "new") {
      setVendorId(undefined);
    } else {
      setVendorId(id);
    }
  }, [id]);

  const derivedError = useMemo(() => {
    const doesNotHaveRequiredVendorMetaData = vendorMetaAttributesRequired.some(
      (key) => {
        return !changes[key] && !vendor?.VendorMeta?.[key];
      }
    );

    if (changes.isSustainableVendor && doesNotHaveRequiredVendorMetaData) {
      return new Error(
        "Sustainable vendors require description, cover image, and avatar"
      );
    }

    if (
      hasNewOffer &&
      (!changes["offerTitle:new"] || !changes["offerStartDate:new"] || !changes["offerImage:new"])
    ) {
      return new Error("Image, title, and start date are required for new offers");
    }

    return null;
  }, [changes, hasNewOffer, vendor?.VendorMeta]);

  useEffect(() => {
    setError(vendorFetchError ?? vendorUpdateError);
  }, [vendorFetchError, vendorUpdateError]);

  const error = storedError ?? derivedError;

  const hasChanges = useMemo(() => {
    if (!vendor) {
      return false;
    }
    return Object.entries(changes).some(([key, value]) => {
      if (key === "tags") {
        return value !== vendor[key]?.join(", ");
      }

      if (key === "relatedGenieCollections") {
        return true;
      }

      return vendor?.VendorMeta?.[key] !== value;
    });
  }, [changes, vendor]);

  const clearChanges = () => {
    setChanges({});
  };

  const onImageUpload = async (e) => {
    const file = e.target.files[0];
    const imageType = e.target.name;
    const resp = await uploadImage({ vendorId, imageType, image: file });

    const [fieldPath, targetId] = imageType.split(":");
    if (fieldPath?.startsWith("metadata.")) {
      const metaKey = fieldPath.split(".")[1];
      setChanges((prevChanges) => {
        const existingMetadata = prevChanges[`metadata:${targetId}`] ??
          vendor?.VendorOffers?.find((o) => o.id?.toString() === targetId)?.metadata ??
          {};
        return {
          ...prevChanges,
          [`metadata:${targetId}`]: {
            ...existingMetadata,
            [metaKey]: resp.assetUrl
          }
        };
      });
    } else {
      setChanges((prevChanges) => ({ ...prevChanges, [imageType]: resp.assetUrl }));
    }
  };

  // Immediate coupon upload handler using mutation hook
  const { mutateAsync: uploadCoupons } = useCreateVendorOfferCouponsMutation(vendorId);

  const onUploadCoupons = async (csvContent, offerId) => {
    if (!vendorId || !offerId) {
      throw new Error("Vendor ID and Offer ID are required");
    }

    try {
      const result = await uploadCoupons({
        offerId,
        couponsCSV: csvContent
      });

      // Refetch vendor data to update the coupon list
      await refetch();

      return result;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // Immediate offer save handler
  const onSaveOffer = async (offerId) => {
    if (!vendorId) {
      throw new Error("Vendor ID is required");
    }

    try {
      const isNewOffer = offerId === "new";

      // Collect all changes for this offer
      const offerChanges = {};
      let newMetadata = null;

      Object.keys(changes).forEach(key => {
        if (key.endsWith(`:${offerId}`)) {
          const [fieldName] = key.split(":");

          if (fieldName === "metadata") {
            // Metadata is stored as an object under the key `metadata:${offerId}`
            newMetadata = changes[key];
          } else if (fieldName === "offerImage" || fieldName === "offerTitle" ||
            fieldName === "offerDescription" || fieldName === "offerUrl" ||
            fieldName === "offerStartDate" || fieldName === "offerEndDate" ||
            fieldName === "dollarAmount" || fieldName === "percentAmount" || fieldName === "pointAmount" ||
            fieldName === "rewardRedemption" || fieldName === "couponCode" ||
            fieldName === "couponCSV" ||
            fieldName === "isVisible") {
            offerChanges[fieldName] = changes[key];
          }
        }
      });

      const existingOffer = !isNewOffer
        ? vendor?.VendorOffers?.find((o) => o.id?.toString() === offerId?.toString())
        : null;

      const offerData = buildOfferPayload({
        offerChanges,
        metadataChanges: newMetadata,
        existingOffer,
        isNewOffer,
        vendorImageURL: changes.imageURL ?? vendor?.VendorMeta?.imageURL
      });
      if (isNewOffer) {
        // Create new offer
        await createOffer(offerData);
      } else {
        // Update existing offer
        await updateOffer({ id: offerId, changes: offerData });
      }

      // Clear changes for this offer
      setChanges((prevChanges) => {
        const newChanges = { ...prevChanges };
        Object.keys(newChanges).forEach(key => {
          if (key.endsWith(`:${offerId}`)) {
            delete newChanges[key];
          }
        });
        return newChanges;
      });

      // Refetch vendor data to show updated offer
      await refetch();

      // Close modal on successful creation
      if (isNewOffer) {
        closeOffer();
      }
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const {
    mutateAsync: generateMetadata,
    isLoading: isGeneratingMetadata
  } = useGenerateVendorMetadata();

  const handleGenerateMetadata = async () => {
    const metadata = await generateMetadata({
      vendorName: changes.vendorName ?? vendor.vendorName
    });

    setChanges((prev) => ({
      description: vendor?.VendorMeta?.description || metadata?.description,
      tags: vendor?.VendorMeta?.tags || metadata?.tags,
      websiteURL: vendor?.VendorMeta?.websiteURL || metadata?.websiteURL,
      affiliateURL: vendor?.VendorMeta?.affiliateURL || metadata?.affiliateURL,
      email: vendor?.VendorMeta?.email || metadata?.email,
      ...prev
    }));
  };

  const { mutateAsync: createVendorCertification } =
    useCreateVendorCertificationMutation({
      vendorId
    });

  const {
    mutateAsync: updateVendorCertification,
    isLoading: isCertificationUpdating
  } = useUpdateVendorCertificationMutation({
    vendorId
  });

  const onSaveChanges = async () => {
    const changeSegments = separateObjectByPrefix(changes, [
      "certification"
    ]);
    const vendorChanges = changeSegments.rest ?? {};
    const certificationChangesById = changeSegments.certification ?? {};

    const hasVendorChanges = Object.keys(vendorChanges).length > 0;
    const hasCertificationChanges =
      Object.keys(certificationChangesById).length > 0;

    if (hasCertificationChanges) {
      await Promise.allSettled(
        Object.entries(certificationChangesById).map(async ([id, changes]) =>
          updateVendorCertification({
            certificationId: id,
            ...changes
          })
        )
      );
    }

    if (hasVendorChanges) {
      const resp = await updateVendor({
        vendorId,
        update: vendorChanges
      });
      if (resp?.vendor?.id && !vendorId) {
        navigate(`/vendors/${resp.vendor.id}`);
        return;
      }
    }

    if (hasVendorChanges || hasCertificationChanges) {
      setChanges({});
      await refetch();
    }
  };

  if (isLoading && vendorId) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pt: 20
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <VendorLayout key={vendorId}>
        <VendorHeader>
          <Box display="flex" flexDirection="column" width="100%">
            <Box
              pb={1}
              display="flex"
              columnGap={2}
              flexDirection="row"
              maxWidth="250px"
            >
              <Box m={0} component="h4" whiteSpace="nowrap">
                {!vendorId ? "Creating" : "Editing"}{" "}
                {changes?.vendorName ?? vendor?.vendorName}
              </Box>
              {hasChanges && (
                <Box m={0} component="small" whiteSpace="nowrap">
                  (Unsaved changes)
                </Box>
              )}
            </Box>
            <Box pb={2} display="flex" columnGap={2} flexDirection="row">
              <Button
                variant="contained"
                disabled={
                  !hasChanges ||
                  isVendorUpdating ||
                  isCertificationUpdating ||
                  error
                }
                onClick={onSaveChanges}
                size="small"
              >
                Save
              </Button>
              <Button
                variant="outlined"
                disabled={!hasChanges || isVendorUpdating}
                onClick={clearChanges}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                disabled={isGeneratingMetadata}
                onClick={handleGenerateMetadata}
                size="small"
              >
                {isGeneratingMetadata ? "Generating…" : "Generate metadata"}
              </Button>
              {vendorId && vendor?.Brand && vendor?.Brand?.id && (
                <a
                  href={`https://brands.thecommons.earth/brand-rating/${vendor?.Brand?.id
                  }/${+new Date()}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button variant="outlined" size="small">
                    View
                  </Button>
                </a>
              )}
              {isVendorUpdating && <CircularProgress />}
            </Box>

            {error && (
              <Box
                borderRadius={2}
                p={1}
                my={1}
                sx={{
                  color: COLORS.ERROR,
                  backgroundColor: COLORS.ERROR_BG
                }}
              >
                <small>{error?.message}</small>
              </Box>
            )}
          </Box>
        </VendorHeader>
        <VendorContent>
          <VendorInputTip title="See hero image guidelines https://www.notion.so/How-to-update-brand-data-in-Potato-1510bc53a62d8040954efd0ddcf61f4b" />
          <ImageUpload
            image={changes.imageURL ?? vendor?.VendorMeta?.imageURL}
            onChange={handleChange}
            imageAlt={vendor?.vendorName}
            sx={{
              mb: 2,
              ...CENTER_COLUMN_STYLES
            }}
            name="imageURL"
          />
          <Box display="flex" flexDirection="row" alignItems="center">
            <ImageUpload
              image={changes.avatarURL ?? vendor?.VendorMeta?.avatarURL}
              onChange={handleChange}
              imageAlt={vendor?.vendorName}
              sx={{
                width: "80px",
                height: "80px",
                borderRadius: "50%"
              }}
              name="avatarURL"
              imgixParams={{
                fit: "clamp",
                width: 160,
                height: 160
              }}
            />
            <VendorInputTip title="Use solid background color. Facebook brand page is a good source. Acceptable image formats: jpg, png. no svg" />
            <Box flex="1">
              <Box pl={2}>
                <Box>
                  <TextField
                    name="vendorName"
                    value={changes.vendorName ?? vendor?.vendorName}
                    onChange={handleChange}
                    label="Name"
                  />
                </Box>
                <Box m={0} width="100%" display="flex">
                  {vendor?.VendorMeta?.sustainableCertifications?.map(
                    (certification) => (
                      <Box
                        key={certification.title}
                        mb={1}
                        mr={1}
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center"
                        }}
                      >
                        <Chip
                          label={certification.title}
                          sx={{
                            backgroundColor: COLORS.LIGHT_GRAY
                          }}
                        />
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box m={0} pt={4} display="flex" flexDirection="column" gap={4}>
            <Box>
              <InputLabel>Description
                <VendorInputTip title="See https://docs.google.com/document/d/1W6vG8cFpDlHZz8jpoWhr34lOJxwkhW37LbRcI_W9PI8/edit?tab=t.0" />
              </InputLabel>
              <Box
                component="textarea"
                style={{
                  ...CENTER_COLUMN_STYLES,
                  height: "100px"
                }}
                maxRows={3}
                onChange={handleChange}
                name="description"
                value={changes?.description ?? vendor?.VendorMeta?.description}
              />
            </Box>
            <Box>
              <InputLabel pb={2}>Tags</InputLabel>
              <Box pt={2}>
                {vendor?.tags?.map((tag) => (
                  <Box key={tag} pb={2} display="inline-flex">
                    <Chip
                      label={tag}
                      sx={{
                        backgroundColor: COLORS.OFFSET,
                        display: "inline-block",
                        marginRight: "8px"
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes.tags ?? vendor?.tags?.join(", ")}
                onChange={handleChange}
                name="tags"
              />
            </Box>
            <Box>
              <InputLabel>Long Description</InputLabel>
              <Box
                component="textarea"
                style={{
                  ...CENTER_COLUMN_STYLES,
                  height: "100px"
                }}
                maxRows={3}
                onChange={handleChange}
                name="longDescription"
                value={
                  changes?.longDescription ??
                  vendor?.VendorMeta?.longDescription
                }
              />
            </Box>
            <Box>
              <InputLabel>Sustainability Description</InputLabel>
              <Box
                component="textarea"
                style={{
                  ...CENTER_COLUMN_STYLES,
                  height: "100px"
                }}
                maxRows={3}
                onChange={handleChange}
                name="sustainableReasonDescription"
                value={
                  changes?.sustainableReasonDescription ??
                  vendor?.VendorMeta?.sustainableReasonDescription
                }
              />
            </Box>
            <Box>
              <InputLabel>Website URL
                <VendorInputTip title="Only change if broken or non-existent. Don't override affiliate links." />
              </InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes?.websiteURL ?? vendor?.VendorMeta?.websiteURL}
                onChange={handleChange}
                name="websiteURL"
                inputProps={{
                  type: "url"
                }}
              />
            </Box>
            <Box>
              <InputLabel>Affiliate URL<VendorInputTip title="Will display instead of Website URL if present" /></InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes?.affiliateURL ?? vendor?.VendorMeta?.affiliateURL}
                onChange={handleChange}
                name="affiliateURL"
                inputProps={{
                  type: "url"
                }}
              />
            </Box>
            <Box>
              <InputLabel>
                Contact Email
                <VendorInputTip title="This email is shown on brand pages when we prompt users to contact a brand" />
              </InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes?.email ?? vendor?.VendorMeta?.email}
                onChange={handleChange}
                name="email"
                inputProps={{
                  type: "email",
                  autoCorrect: "email"
                }}
              />
            </Box>
            <Box>
              <InputLabel>
                Contact URL{" "}
                <VendorInputTip
                  title="If the brand does not expose and email we show this URL instead"
                />
              </InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes?.contactURL ?? vendor?.VendorMeta?.contactURL}
                onChange={handleChange}
                name="contactURL"
                inputProps={{
                  type: "url"
                }}
              />
            </Box>
            <Box>
              <InputLabel>Search terms</InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes?.searchTerms ?? vendor?.VendorMeta?.searchTerms}
                onChange={handleChange}
                name="searchTerms"
              />
            </Box>
            <Box>
              <InputLabel>Learn more URL</InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={
                  changes?.emissionsLearnMoreURL ??
                  vendor?.VendorMeta?.emissionsLearnMoreURL
                }
                onChange={handleChange}
                name="emissionsLearnMoreURL"
                inputProps={{
                  type: "url"
                }}
              />
            </Box>
            <Box>
              <InputLabel id="companySize">Company Size</InputLabel>
              <Select
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                labelId="companySize"
                value={(() => {
                  const currentValue = Object.prototype.hasOwnProperty.call(changes, "companySize")
                    ? changes.companySize
                    : vendor?.VendorMeta?.companySize;
                  return currentValue || "";
                })()}
                onChange={(e) => {
                  setError(null);
                  const value = e.target.value === "" ? null : e.target.value;
                  setChanges({ ...changes, companySize: value });
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </Select>
            </Box>
            <Box>
              <InputLabel>Parent Entity</InputLabel>
              <TextField
                sx={{
                  ...CENTER_COLUMN_STYLES
                }}
                value={changes?.parentEntity ?? vendor?.VendorMeta?.parentEntity ?? ""}
                onChange={handleChange}
                name="parentEntity"
              />
            </Box>
          </Box>
        </VendorContent>
        <VendorSidebar>
          <Box mb={2}>
            <SidebarModule title="Vendor Info" isExpanded>
              <SubcategorySelect
                value={changes.joroSubcategoryId ?? vendor.joroSubcategoryId}
                onChange={handleChange}
              />
              <SidebarModuleCheck
                label="Partner"
                isChecked={changes.isPartner ?? vendor?.isPartner ?? false}
                onChange={handleChange}
                name="isPartner"
              />
              <Typography variant="body2" pt={2}>
                Vendor id: <strong>{vendor?.id ?? "none"}</strong>
              </Typography>
              {vendor?.id && (
                <Box pt={2}>
                  <Typography variant="body2">Vendor Attributes:</Typography>
                  <VendorAttributes vendorId={vendor.id} />
                </Box>
              )}

              {vendor?.Brand
                ? (
                  <Box pt={2}>
                    <Typography>
                      Brand id: <Link href={`/brands/${vendor?.Brand.id}`} style={{ textDecoration: "underline" }}><strong>{vendor?.Brand.id}</strong></Link>
                    </Typography>
                  </Box>
                )
                : (
                  <Box mt={2}>
                    {vendor.id && (
                      <Button
                        variant="contained"
                        onClick={async () => {
                          await updateVendor({
                            vendorId: vendor?.id,
                            update: { shouldCreateBrand: true }
                          });
                          await refetch();
                        }}
                      >
                        Add to brands
                      </Button>
                    )}
                  </Box>
                )}
            </SidebarModule>
            {vendor?.Brand && (
              <SidebarModule title="Review" isExpanded>
                <Box>
                  <InputLabel id="reviewStatus">Review Status</InputLabel>
                  <Select
                    sx={{
                      width: "100%"
                    }}
                    labelId="reviewStatus"
                    value={
                      changes.reviewStatus ?? vendor?.Brand?.reviewStatus ?? ""
                    }
                    onChange={(e) => {
                      handleChange({
                        target: {
                          name: "reviewStatus",
                          value: e.target.value
                        }
                      });
                    }}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="PendingReview">Pending Review</MenuItem>
                    <MenuItem value="Reviewing">Reviewing</MenuItem>
                    <MenuItem value="Reviewed">Reviewed</MenuItem>
                  </Select>
                </Box>
              </SidebarModule>
            )}
            {
              vendor?.Brand && (
                <>
                  <SidebarModule title="Alternatives" >
                    <Box>
                      <InputLabel id="recommendedAlternatives">Recommended Alternatives</InputLabel>
                      <BrandRecommendations brandId={vendor.Brand.id} />
                    </Box>
                    <Box>
                      <InputLabel id="relatedGenieCollections">Related Genie Collections</InputLabel>
                      {isLoadingGenieCollections && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>Loading collections...</Typography>
                      )}

                      <Box sx={{ mb: 2 }}>
                        {(() => {
                          const currentCollections = changes.relatedGenieCollections ?? vendor?.Brand?.relatedGenieCollections ?? [];
                          const canDelete = currentCollections.length > 1;

                          return currentCollections.map((collection, index) => {
                            const collectionData = genieCollectionsData?.collections?.find(
                              c => c.id === (collection.GenieCollection?.id ?? collection.genieCollectionId)
                            );

                            return (
                              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {collectionData?.displayName ?? `Collection ${collection.GenieCollection?.id ?? collection.genieCollectionId}`}
                                </Typography>
                                <TextField
                                  placeholder="Type"
                                  value={collection.type ?? ""}
                                  onChange={(e) => handleCollectionTypeChange(index, e.target.value)}
                                  size="small"
                                  sx={{ width: "150px" }}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRemoveCollection(index)}
                                  disabled={!canDelete}
                                >
                                  <Delete fontSize="small" />
                                </Button>
                              </Box>
                            );
                          });
                        })()}
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <Select
                          sx={{ flex: 1 }}
                          value={newCollectionId}
                          onChange={(e) => setNewCollectionId(e.target.value)}
                          disabled={isLoadingGenieCollections}
                          size="small"
                          displayEmpty
                        >
                          <MenuItem value="">Select Collection</MenuItem>
                          {genieCollectionsData?.collections?.map(collection => (
                            <MenuItem key={collection.id} value={collection.id}>
                              {collection.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          placeholder="Type"
                          value={newCollectionType}
                          onChange={(e) => setNewCollectionType(e.target.value)}
                          size="small"
                          sx={{ width: "150px" }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleAddCollection}
                          disabled={!newCollectionId}
                        >
                          <Add fontSize="small" />
                        </Button>
                      </Box>
                    </Box>
                  </SidebarModule>
                  <SidebarModule title="Attributes" >
                    <SidebarModuleCheck
                      label="Searchable"
                      isChecked={
                        changes.searchable ??
                        vendor?.Brand?.searchable ??
                        false
                      }
                      onChange={handleChange}
                      name="searchable"
                      description="Controls whether this brand appears in search results"
                    />
                    <SidebarModuleCheck
                      label="Display on Web"
                      isChecked={
                        changes.displayWeb ??
                        vendor?.Brand?.displayWeb ??
                        false
                      }
                      onChange={handleChange}
                      name="displayWeb"
                      description="Controls whether this brand is displayed on the web"
                    />
                    <Box pt={2}>
                      <InputLabel id="brandAttributes">Brand Attributes</InputLabel>
                      <BrandAttributes brandId={vendor.Brand.id} />
                    </Box>
                  </SidebarModule>
                </>
              )
            }
            <SidebarModule title="Carbonizer Settings">
              <SidebarModuleCheck
                label="Clean Name"
                isChecked={
                  changes.isCleanVendorName ??
                  vendor?.isCleanVendorName ??
                  false
                }
                onChange={handleChange}
                name="isCleanVendorName"
                description="This denotes this is the proper name for the vendor. If false a corrected vendor can be mapped to. Only mark one per brand, using cleanest name (e.g. REI vs. Rei Manhattan Br)."
              />
              {!vendor?.isCleanVendorName && (
                <VendorSelect
                  value={changes.correctedVendorId ?? vendor.correctedVendorId}
                  onChange={handleChange}
                  label="Corrected Vendor"
                  name="correctedVendorId"
                />
              )}
              <Box>
                <InputLabel id="isMatchable">Matchable</InputLabel>
                <Select
                  sx={{ width: "100%", mb: 2 }}
                  labelId="isMatchable"
                  value={(() => {
                    const currentValue = Object.prototype.hasOwnProperty.call(changes, "isMatchable")
                      ? changes.isMatchable
                      : vendor?.isMatchable;
                    return currentValue === null || currentValue === undefined ? "" : String(currentValue);
                  })()}
                  onChange={(e) => {
                    setError(null);
                    let value;
                    if (e.target.value === "") {
                      value = null;
                    } else {
                      value = e.target.value === "true";
                    }
                    setChanges({ ...changes, isMatchable: value });
                  }}
                >
                  <MenuItem value="">Not Set</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </Box>
              <Box columnGap={2} pt={2} display="flex" alignItems={"center"}>
                {vendorId && (
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`/vendors/${vendorId}/memo-tester`}
                  >
                    Memo Tester
                  </Button>
                )}
                {vendorId && vendor?.isCleanVendorName && (
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`/vendors/${vendorId}/corrected-vendors`}
                  >
                    Corrected Vendors
                  </Button>
                )}
              </Box>
            </SidebarModule>
            <SidebarModule title="Certifications">
              {!vendorId
                ? (
                  <Typography variant="body6">
                    Create vendor before adding certifications
                  </Typography>
                )
                : (
                  <>
                    <CertificationButton
                      onSelectCertification={async (certificate) => {
                        await createVendorCertification({
                          certificationId: certificate.value
                        });
                        await refetch();
                      }}
                      mb={2}
                      exclude={vendor?.VendorCertifications?.map(
                        (cert) => cert?.Certification?.id
                      )}
                    />
                    {vendor?.VendorCertifications?.map((vendorCertification) => (
                      <CertificationModule
                        key={vendorCertification.id}
                        onChange={handleChange}
                        title={vendorCertification?.Certification?.title}
                        id={vendorCertification.id}
                        certificationId={vendorCertification?.Certification?.id}
                        certificationURL={
                          changes[
                            `certificationURL:${vendorCertification?.Certification?.id}`
                          ] ?? vendorCertification.certificationURL
                        }
                        certificationStatus={
                          changes[
                            `certificationStatus:${vendorCertification?.Certification?.id}`
                          ] ?? vendorCertification.certificationStatus
                        }
                      />
                    ))}
                  </>
                )}
            </SidebarModule>
            <SidebarModule title="Offers">
              {!vendorId
                ? (
                  <Typography variant="h6">
                    Save vendor before adding offers
                  </Typography>
                )
                : (
                  <>
                    <Box marginBottom={2}>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          if (!hasNewOffer) {
                            setChanges((prevChanges) => ({
                              ...prevChanges,
                              "metadata:new": { offerType: "gift card" }

                            }));
                          }
                          selectOffer("new");
                        }}
                      >
                        + {hasNewOffer ? "Edit new" : "Add"} Offer
                      </Button>
                    </Box>
                    <OfferSection
                      offers={rewardOffers}
                      title="Rewards Redeemable"
                      onSelectOffer={selectOffer}
                    />
                    <OfferSection
                      offers={nonRewardOffers}
                      title="Non-Rewards Redeemable"
                      onSelectOffer={selectOffer}
                    />
                  </>
                )}
            </SidebarModule>
          </Box>
        </VendorSidebar>
      </VendorLayout>
      <Modal open={!!selectedOffer}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100vw", height: "100vh" }}
          onClick={closeOffer}
          data-testid="offer-modal"
        >
          <Card
            onClick={(e) => e.stopPropagation()}
            sx={{ maxHeight: "80vh", overflowY: "scroll", width: "90%", maxWidth: "800px" }}
          >
            {(() => {
              const offer = vendor?.VendorOffers?.find(
                (offer) => offer.id.toString() === selectedOffer
              );
              const isNewOffer = selectedOffer === "new";
              const id = offer?.id || selectedOffer;

              return offer || isNewOffer
                ? (
                  <OfferModal
                    onChange={handleChange}
                    onImageUpload={onImageUpload}
                    onUploadCoupons={onUploadCoupons}
                    onSaveOffer={() => onSaveOffer(id)}
                    isSavingOffer={isCreatingOffer || isUpdatingOffer}
                    id={id ?? selectOffer}
                    offerTitle={changes[`offerTitle:${id}`] ?? offer?.offerTitle}
                    offerDescription={
                      changes[`offerDescription:${id}`] ?? offer?.offerDescription
                    }
                    offerUrl={changes[`offerUrl:${id}`] ?? offer?.offerUrl}
                    offerEndDate={
                      changes[`offerEndDate:${id}`] ?? offer?.offerEndDate
                    }
                    offerStartDate={
                      changes[`offerStartDate:${id}`] ?? offer?.offerStartDate
                    }
                    offerImage={
                      changes[`offerImage:${id}`] ??
                      offer?.offerImage
                    }
                    dollarAmount={changes[`dollarAmount:${id}`] ?? offer?.dollarAmount}
                    percentAmount={changes[`percentAmount:${id}`] ?? offer?.percentAmount}
                    pointAmount={changes[`pointAmount:${id}`] ?? offer?.pointAmount}
                    rewardRedemption={changes[`rewardRedemption:${id}`] ?? offer?.rewardRedemption}
                    metadata={changes[`metadata:${id}`] ?? offer?.metadata ?? {}}
                    couponCode={changes[`couponCode:${id}`] ?? offer?.couponCode}
                    offer={offer}
                    isVisible={changes[`isVisible:${id}`] ?? offer?.isVisible}
                    vendorWebsiteURL={vendor?.VendorMeta?.websiteURL}
                    vendorImageURL={changes.imageURL ?? vendor?.VendorMeta?.imageURL}
                  />
                )
                : (
                  <Box padding={6}>
                    <Typography variant="h3" fontWeight="bold">
                      Offer not found
                    </Typography>
                  </Box>
                );
            })()}
          </Card>
        </Box>
      </Modal>
    </>
  );
};
